// src/pages/PricingPage.js
import React, { useContext } from 'react';
import styled from 'styled-components';
import TopText from '../components/TopText';
import { FaTag, FaSeedling, FaGem, FaCrown } from 'react-icons/fa';
import { AuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

// Toggle this flag to enable/disable pricing page
const PRICING_ENABLED = true;

const OuterContainer = styled.div`
  max-width: 100%;
  overflow-x: hidden;
  background-color: #E3E3E3;
  min-height: calc(100vh - 120px);
  border-radius: 12px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 50px 20px 40px;

  @media (max-width: 768px) {
    padding: 50px 20px 20px;
  }
`;

const ContentContainer = styled.div`
  width: 75%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0;
  font-family: 'Rubik', sans-serif;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const PricingGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin: 30px 0;
  width: 100%;
`;

const PricingCard = styled.div`
  background: white;
  border: 1px solid rgba(46, 46, 46, 0.2);
  border-radius: 12px;
  padding: 30px;
  width: 100%;
  display: flex;
  gap: 60px;
  transition: all 0.3s ease;
  position: relative;
  overflow: visible;
  min-height: 160px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    padding: 20px;
    gap: 20px;
    min-height: auto;
  }
  
  ${props => props.$featured && `
    position: relative;
    
    &::before {
      content: '';
      position: absolute;
      top: -1px;
      left: -1px;
      right: -1px;
      bottom: -1px;
      background: linear-gradient(
        to right,
        #2E2E2E,
        #444,
        #2E2E2E
      );
      border-radius: 12px;
      z-index: -1;
    }
    
    &::after {
      content: '';
      position: absolute;
      top: 1px;
      left: 1px;
      right: 1px;
      bottom: 1px;
      background: linear-gradient(
        125deg,
        #ffffff 0%,
        #f8f8f8 25%,
        #ffffff 35%,
        #f8f8f8 45%,
        #ffffff 50%,
        #f8f8f8 55%,
        #ffffff 65%,
        #f8f8f8 75%,
        #ffffff 100%
      );
      background-size: 200% 200%;
      background-position: 0% 0%;
      border-radius: 11px;
      z-index: -1;
      transition: all 1.5s ease;
    }
    
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  `}

  &:hover {
    border-color: rgba(46, 46, 46, 0.4);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    
    &::after {
      background-position: 100% 100%;
    }
  }
`;

const TierSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 220px;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    min-width: auto;
    width: 100%;
    align-items: center;
  }
`;

const TierHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;

const TierIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  flex-shrink: 0;
  
  svg {
    font-size: 28px;
    color: ${props => props.$featured ? '#2E2E2E' : props.$color || '#2E2E2E'};
    transition: transform 0.3s ease;
  }

  ${props => props.$featured && `
    svg {
      filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
    }
  `}
`;

const TierInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const TierName = styled.h2`
  font-family: 'DM Serif Text', serif;
  color: #2E2E2E;
  margin: 0;
  font-size: 20px;
  
  @media (min-width: 768px) {
    font-size: 24px;
  }
`;

const Price = styled.div`
  font-family: 'Rubik', sans-serif;
  color: #6A6A6A;
  
  .amount {
    font-size: 24px;
    font-weight: bold;
    color: #2E2E2E;
  }
  
  .period {
    font-size: 14px;
  }
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 20px 40px;
  margin: 0;
  flex: 1;
  min-width: 400px;

  li {
    font-family: 'Rubik', sans-serif;
    color: #6A6A6A;
    margin: 12px 0;
    font-size: 15px;
    
    @media (min-width: 768px) {
      font-size: 16px;
    }
  }

  @media (max-width: 768px) {
    text-align: center;
    padding: 10px 20px;
    min-width: auto;
  }
`;

const SubscribeButton = styled.button`
  padding: 12px 24px;
  background-color: ${props => props.$featured ? '#2E2E2E' : 'white'};
  color: ${props => props.$featured ? 'white' : '#2E2E2E'};
  border: 2px solid #2E2E2E;
  border-radius: 10px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  font-family: 'Rubik', sans-serif;
  transition: all 0.3s;
  width: 100%;

  &:hover {
    background-color: ${props => props.$featured ? '#444' : '#2E2E2E'};
    color: white;
    transform: translateY(-1px);
  }
`;

const DisabledButton = styled(SubscribeButton)`
  background-color: rgba(46, 46, 46, 0.4);
  border-color: transparent;
  color: white;
  cursor: not-allowed;

  &:hover {
    background-color: rgba(46, 46, 46, 0.4);
    transform: none;
  }
`;

const PopularBadge = styled.div`
  position: absolute;
  top: 15px;
  right: -10px;
  background: linear-gradient(90deg, #2E2E2E, #444);
  color: white;
  padding: 4px 12px;
  border-radius: 4px;
  font-family: 'Rubik', sans-serif;
  font-size: 12px;
  z-index: 2;
  white-space: nowrap;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  &::after {
    content: '';
    position: absolute;
    right: 0;
    bottom: -5px;
    width: 10px;
    height: 5px;
    background-color: #1a1a1a;
    clip-path: polygon(0 0, 0 100%, 100% 0);
  }
  
  @media (max-width: 768px) {
    left: 50%;
    right: auto;
    top: -12px;
    transform: translateX(-50%);
    border-radius: 12px;
    
    &::after {
      display: none;
    }
  }
`;

// Add these constants at the top level
const STRIPE_PRICES = {
  Pro: 'price_1QwybVAMCn7sSNAisMgMhdnz',
  Premier: 'price_1Qwye4AMCn7sSNAi5MD4TViz'
};

// Add payment URL constant
const PAYMENTS_URL = process.env.REACT_APP_PAYMENTS_URL || 'https://tempostock.com/api/payments';

const ComingSoonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  text-align: center;
  padding: 20px;
`;

const ComingSoonText = styled.h2`
  font-family: 'DM Serif Text', serif;
  color: #2E2E2E;
  font-size: 36px;
  margin-bottom: 16px;
`;

const ComingSoonSubtext = styled.p`
  font-family: 'Rubik', sans-serif;
  color: #6A6A6A;
  font-size: 18px;
  max-width: 600px;
  line-height: 1.6;
`;

// Update the TopText wrapper to match the new style
const TopTextWrapper = styled.div`
  margin-bottom: 20px;
`;

function PricingPage() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const handleSignUp = () => {
    navigate('/login?tab=signup');
  };

  const handleSubscription = async (plan) => {
    if (!user) {
      handleSignUp();
      return;
    }

    // Debug user object
    console.log('User object:', {
      id: user.id,
      email: user.email,
      account_type: user.account_type,
      fullObject: JSON.stringify(user, null, 2)
    });
    
    if (!user.id) {
      console.error('User ID is missing from user object. Please try logging out and logging back in.');
      return;
    }

    try {
      const checkoutUrl = `${PAYMENTS_URL}/create-checkout-session`;
      console.log('Making request to:', checkoutUrl);
      const payload = {
        priceId: STRIPE_PRICES[plan],
        userId: user.id,
      };
      console.log('Request payload:', payload);
      
      const response = await fetch(checkoutUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Response not OK:', {
          status: response.status,
          statusText: response.statusText,
          body: errorText
        });
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Checkout session response:', data);
      
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      // TODO: Add proper error handling/notification here
    }
  };

  // Helper function to determine button text based on current plan and target plan
  const getButtonText = (targetPlan) => {
    if (!user) return 'Sign Up';
    
    if (user.account_type === targetPlan) return 'Current Plan';
    
    if (user.account_type === 'Free') return 'Upgrade';
    
    const planRanking = { Free: 1, Pro: 2, Premier: 3 };
    return planRanking[targetPlan] < planRanking[user.account_type] ? 'Downgrade' : 'Upgrade';
  };

  const handleButtonClick = (plan) => {
    if (plan === 'Free') {
      // Handle downgrade to free plan
      if (!user) {
        handleSignUp();
        return;
      }

      if (user.account_type === 'Free') {
        return; // Already on free plan
      }

      // Show confirmation dialog
      if (!window.confirm('Are you sure you want to cancel your subscription? This will take effect at the end of your current billing period.')) {
        return;
      }

      // Cancel subscription
      fetch(`${PAYMENTS_URL}/cancel-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: user.id,
        }),
      })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          alert('Your subscription has been cancelled and will end at the end of your current billing period.');
          window.location.reload(); // Refresh to update UI
        } else {
          throw new Error(data.error || 'Failed to cancel subscription');
        }
      })
      .catch(error => {
        console.error('Error canceling subscription:', error);
        alert('Failed to cancel subscription. Please try again or contact support.');
      });
      return;
    }
    
    if (!user) {
      handleSignUp();
      return;
    }

    if (user.account_type === plan) {
      return; // Already on this plan
    }

    handleSubscription(plan);
  };

  return (
    <OuterContainer>
      <Helmet>
        <title>Pricing | Tempo Stock</title>
        <meta name="description" content="Choose the perfect plan for your royalty-free music needs with Tempo Stock's flexible pricing options." />
      </Helmet>
      <ContentContainer>
        <TopTextWrapper>
          <TopText Icon={FaTag} text="Pricing" />
        </TopTextWrapper>
        {PRICING_ENABLED ? (
          <PricingGrid>
            <PricingCard>
              <TierSection>
                <TierHeader>
                  <TierIcon $color="#4caf50">
                    <FaSeedling />
                  </TierIcon>
                  <TierInfo>
                    <TierName>Free</TierName>
                    <Price>
                      <span className="amount">$0</span>
                      <span className="period">/month</span>
                    </Price>
                  </TierInfo>
                </TierHeader>
                {user ? (
                  user.account_type === 'Free' ? (
                    <DisabledButton disabled>
                      Current Plan
                    </DisabledButton>
                  ) : (
                    <SubscribeButton onClick={() => handleButtonClick('Free')}>
                      {getButtonText('Free')}
                    </SubscribeButton>
                  )
                ) : (
                  <SubscribeButton onClick={handleSignUp}>
                    Sign Up
                  </SubscribeButton>
                )}
              </TierSection>
              <FeatureList>
                <li>• 1 download per week</li>
                <li>• Must credit Tempo Stock</li>
                <li>• General commercial terms</li>
              </FeatureList>
            </PricingCard>

            <PricingCard $featured>
              <PopularBadge>Most Popular</PopularBadge>
              <TierSection>
                <TierHeader>
                  <TierIcon $featured>
                    <FaGem />
                  </TierIcon>
                  <TierInfo>
                    <TierName>Pro</TierName>
                    <Price>
                      <span className="amount">$4.99</span>
                      <span className="period">/month</span>
                    </Price>
                  </TierInfo>
                </TierHeader>
                {user && user.account_type === 'Pro' ? (
                  <DisabledButton disabled>
                    Current Plan
                  </DisabledButton>
                ) : (
                  <SubscribeButton $featured onClick={() => handleButtonClick('Pro')}>
                    {getButtonText('Pro')}
                  </SubscribeButton>
                )}
              </TierSection>
              <FeatureList>
                <li>• 10 downloads per week</li>
                <li>• No credit to Tempo Stock needed</li>
                <li>• General commercial terms</li>
              </FeatureList>
            </PricingCard>

            <PricingCard>
              <TierSection>
                <TierHeader>
                  <TierIcon>
                    <FaCrown />
                  </TierIcon>
                  <TierInfo>
                    <TierName>Premier</TierName>
                    <Price>
                      <span className="amount">$14.99</span>
                      <span className="period">/month</span>
                    </Price>
                  </TierInfo>
                </TierHeader>
                {user && user.account_type === 'Premier' ? (
                  <DisabledButton disabled>
                    Current Plan
                  </DisabledButton>
                ) : (
                  <SubscribeButton onClick={() => handleButtonClick('Premier')}>
                    {getButtonText('Premier')}
                  </SubscribeButton>
                )}
              </TierSection>
              <FeatureList>
                <li>• Unlimited downloads</li>
                <li>• Everything in Pro</li>
                <li>• Priority support</li>
              </FeatureList>
            </PricingCard>
          </PricingGrid>
        ) : (
          <ComingSoonContainer>
            <ComingSoonText>Coming Soon</ComingSoonText>
            <ComingSoonSubtext>
              We're working on exciting new pricing plans to better serve our community. 
              Check back soon for updates!
            </ComingSoonSubtext>
          </ComingSoonContainer>
        )}
      </ContentContainer>
    </OuterContainer>
  );
}

export default PricingPage;